// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import axios from 'axios';
import { useGuestRideActions } from '../hooks/useGuestRideActions';
import { useGuestRideStore } from '../stores/guestRideStore';
import { authService } from '@features/auth/services/authService';
import { toast } from 'react-toastify';
import { ridesApiError } from '../services/ridesApi';
import * as Sentry from '@sentry/react';
import config from '@config';

import { css } from '@emotion/css';
import { Theme, useTheme, Typography, Button, CircularProgress } from '@mui/material';
import { BiSolidPhoneCall, BiSolidInfoCircle, BiSolidError } from 'react-icons/bi';
import { Map, useMap } from '@vis.gl/react-google-maps';
import { LocationPinMarker } from '@components/Maps';

import Modal from '@components/ModalComponents/Modal';
import ModalHeader from '@components/ModalComponents/ModalHeader';
import ModalContent from '@components/ModalComponents/ModalContent';
import ModalFooter from '@components/ModalComponents/ModalFooter';

// TYPES ---------------------------------------------------------------- //

interface CallRideModalProps {
  open: boolean;
  onClose: () => void;
  ride: any;
  isRetry?: boolean;
}

// MAP FIT BOUNDS HELPER -------------------------------------------------------- //

const MapFitBounds: React.FC<{
  pickup: { lat: number; lng: number } | null;
  dropoff: { lat: number; lng: number } | null;
}> = ({ pickup, dropoff }) => {
  const map = useMap('call-ride-modal-map');

  React.useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (pickup && dropoff) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(pickup);
      bounds.extend(dropoff);
      map.fitBounds(bounds, 60);
    } else if (pickup) {
      map.panTo(pickup);
      map.setZoom(14);
    } else if (dropoff) {
      map.panTo(dropoff);
      map.setZoom(14);
    }
  }, [map, pickup, dropoff]);

  return null;
};

// COMPONENT ---------------------------------------------------------------- //

const CallRideModal: React.FC<CallRideModalProps> = ({ open, onClose, ride, isRetry = false }) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const { refetch } = useGuestRideActions();
  const markRideCalled = useGuestRideStore((s) => s.markRideCalled);
  const [loading, setLoading] = React.useState(false);

  const pickup =
    ride?.pickup_lat != null && ride?.pickup_lng != null
      ? { lat: Number(ride.pickup_lat), lng: Number(ride.pickup_lng), address: ride?.pickup_address || '' }
      : null;
  const dropoff =
    ride?.dropoff_lat != null && ride?.dropoff_lng != null
      ? { lat: Number(ride.dropoff_lat), lng: Number(ride.dropoff_lng), address: ride?.dropoff_address || '' }
      : null;
  const hasMap = !!(pickup || dropoff);
  const mapCenter = pickup && dropoff
    ? { lat: (pickup.lat + dropoff.lat) / 2, lng: (pickup.lng + dropoff.lng) / 2 }
    : pickup || dropoff || { lat: 32.7767, lng: -96.797 };

  const handleCallRide = async () => {
    setLoading(true);
    try {
      const token = await authService.getAccessTokenWithAutoRefresh();

      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const baseUrl = isDevelopment ? window.location.origin : config.hopridesBaseUrl;

      const endpoint = isDevelopment ? `${baseUrl}/.netlify/functions/app-call-ride` : `${baseUrl}/v1/app-call-ride`;

      const response = await axios({
        method: 'POST',
        url: endpoint,
        data: {
          guestrideId: ride?.id,
          provider: ride?.provider,
          productType: ride?.config?.productType,
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.data?.success === true && response.data.awaiting_approval) {
        // The rooftop requires manager approval: the ride is held, not booked, so it stays `new`.
        toast.success('Sent for manager approval. The ride books once it is approved.');
        onClose();
        if (refetch) refetch();
      } else if (response?.data?.success === true) {
        toast.success(
          isRetry ? 'Ride retried successfully! Awaiting response...' : 'Ride called successfully! Awaiting response...'
        );
        // Optimistically flip the ride to `pending` so the Call button doesn't
        // linger and invite a second click while the server catches up.
        if (ride?.id != null) markRideCalled(ride.id, ride.status);
        onClose();
        if (refetch) refetch();
      } else {
        console.error('Failed to call ride:', response.data);
        Sentry.captureException(new Error('app-call-ride returned an unsuccessful response'), {
          tags: { feature: 'guest-rides', action: isRetry ? 'retry-ride' : 'call-ride' },
          extra: {
            rideId: ride?.id,
            status: ride?.status,
            provider: ride?.provider,
            productType: ride?.config?.productType,
            response: response?.data,
          },
        });
        toast.error(isRetry ? 'Failed to retry ride' : 'Failed to call ride');
      }
    } catch (err) {
      const refusal = ridesApiError(err);
      if (refusal.status === 409 && refusal.message) {
        // A refusal the rides service explains, such as a denied ride. Not an error to report.
        toast.error(refusal.message);
        onClose();
        if (refetch) refetch();
        return;
      }
      console.error('Error calling ride:', err);
      Sentry.captureException(err, {
        tags: { feature: 'guest-rides', action: isRetry ? 'retry-ride' : 'call-ride' },
        extra: {
          rideId: ride?.id,
          status: ride?.status,
          provider: ride?.provider,
          productType: ride?.config?.productType,
        },
      });
      toast.error(isRetry ? 'Failed to retry ride' : 'Failed to call ride');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} handleClose={onClose} width='sm'>
      <ModalHeader handleClose={onClose}>{isRetry ? 'Retry Ride' : 'Call Ride'}</ModalHeader>

      <ModalContent>
        {/* Info / Warning blurb */}
        <div className={isRetry ? cls.blurbWarning : cls.blurb}>
          {isRetry ? (
            <BiSolidError className={cls.blurbIconWarning} />
          ) : (
            <BiSolidInfoCircle className={cls.blurbIcon} />
          )}
          <Typography
            variant='body2'
            sx={{ textWrap: 'pretty', color: isRetry ? theme.palette.warning.dark : theme.palette.text.secondary }}
          >
            {isRetry
              ? `The previous ride attempt for ${ride?.guest_name || 'this guest'} has failed. This will attempt to call a new ride with the same details.`
              : `This will hail a ride for ${ride?.guest_name || 'this guest'}.`}
          </Typography>
        </div>

        {/* Map preview */}
        {hasMap && (
          <div className={cls.mapContainer}>
            <Map
              id='call-ride-modal-map'
              mapId='a1bb97db90b89ef9'
              defaultCenter={mapCenter}
              defaultZoom={pickup && dropoff ? 12 : 14}
              disableDefaultUI
              gestureHandling='cooperative'
              clickableIcons={false}
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={false}
              style={{ width: '100%', height: '100%' }}
            >
              <MapFitBounds pickup={pickup} dropoff={dropoff} />
              {pickup && (
                <LocationPinMarker
                  mapId='call-ride-modal-map'
                  markerId='pickup'
                  position={{ lat: pickup.lat, lng: pickup.lng }}
                  size={40}
                  iconSize={22}
                />
              )}
              {dropoff && (
                <LocationPinMarker
                  mapId='call-ride-modal-map'
                  markerId='delivery'
                  position={{ lat: dropoff.lat, lng: dropoff.lng }}
                  size={40}
                  iconSize={22}
                />
              )}
            </Map>
          </div>
        )}

        {/* Pickup / Dropoff details */}
        <Typography variant='body2' color='text.secondary' gutterBottom>
          <strong>Pickup:</strong> {ride?.pickup_address || 'No address provided'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          <strong>Dropoff:</strong> {ride?.dropoff_address || 'No address provided'}
        </Typography>
      </ModalContent>

      <ModalFooter>
        <Button disabled={loading} variant='text' color='utility' onClick={onClose}>
          Close
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleCallRide}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color='inherit' /> : <BiSolidPhoneCall />}
        >
          {loading ? (isRetry ? 'Retrying...' : 'Calling...') : isRetry ? 'Retry Ride' : 'Call Ride'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  blurb: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.divider};
    border-radius: 10px;
    margin-bottom: 12px;
    background-color: ${theme?.palette?.action?.hover};
  `,
  blurbWarning: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid ${theme?.palette?.warning?.light};
    border-radius: 10px;
    margin-bottom: 12px;
    background-color: ${theme?.palette?.warning?.light}20;
  `,
  blurbIcon: css`
    min-width: 20px;
    min-height: 20px;
    font-size: 20px;
    color: ${theme?.palette?.text?.secondary};
  `,
  blurbIconWarning: css`
    min-width: 20px;
    min-height: 20px;
    font-size: 20px;
    color: ${theme?.palette?.warning?.main};
  `,
  mapContainer: css`
    height: 220px;
    margin-bottom: 12px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${theme?.palette?.utility?.lighter};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default CallRideModal;
